import {WBPopupTrigger} from '@wandb/ui';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {makeDidPropsOrStateChange} from '@wandb/weave/common/util/shouldUpdate';
import {Button} from '@wandb/weave/components/Button';
import {WorkspaceTag} from '@wandb/weave/components/Tag/WorkspaceTag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import produce from 'immer';
import * as _ from 'lodash';
import memoize from 'memoize-one';
import React, {useCallback, useMemo} from 'react';
import {Link, Redirect} from 'react-router-dom';

import BulkTagEditor, {TagState} from '../../components/Tags/BulkTagEditor';
import * as Generated from '../../generated/graphql';
import {
  combinedFilter,
  useBulkEditorProjectTagsQuery,
  useRunSelectorQuery,
} from '../../state/graphql/runSelectorQuery';
import {useSelectedCounts} from '../../state/graphql/selectedCountQuery';
import {useDeepMemo, useDispatch} from '../../state/hooks';
import * as RunHooks from '../../state/runs/hooks';
import {useRunsQueryContext} from '../../state/runs/hooks';
import {setCreateSweepArgs} from '../../state/sweeps/actions';
import * as ViewerHooks from '../../state/viewer/hooks';
import * as CustomRunColorsTypes from '../../state/views/customRunColors/types';
import * as FilterActions from '../../state/views/filter/actions';
import * as GroupSelectionsActions from '../../state/views/groupSelections/actions';
import * as ViewHooks from '../../state/views/hooks';
import * as RunSetActions from '../../state/views/runSet/actions';
import * as RunSetTypes from '../../state/views/runSet/types';
import * as SortActions from '../../state/views/sort/actions';
import * as TempSelectionsActions from '../../state/views/tempSelections/actions';
import * as TempSelectionsTypes from '../../state/views/tempSelections/types';
import {BenchmarkProject} from '../../types/graphql';
import * as Query from '../../util/queryts';
import {
  Config as TableSettings,
  EMPTY_CONFIG as EMPTY_TABLE_SETTINGS,
} from '../../util/runfeed';
import * as RunHelpers2 from '../../util/runhelpers';
import * as Run from '../../util/runs';
import {formatTimestamp} from '../../util/runs';
import {getSweepDisplayName} from '../../util/sweeps';
import * as TableCols from '../../util/tablecols';
import * as urls from '../../util/urls';
import {CreateSweepButton} from '../CreateSweepButton';
import {DeleteRunsButton} from '../DeleteRunsButton';
import {EditableLabel} from '../elements/EditableLabel';
import {TimeDisplay} from '../elements/TimeDisplay';
import {RunTagInfiniteDropdown} from '../InfiniteScrollDropdown/RunTagComponents';
import {JobDrawerLocked} from '../Launch/JobDrawer/JobDrawer';
import {JobInfo, LaunchDrawerJobRun} from '../Launch/utilTypes';
import {ProjectPicker} from '../ProjectPicker/ProjectPicker';
import {RunMover} from '../RunMover';
import {RunsSearchBar} from '../RunsSearchBar';
import {RunState} from '../RunState';
import {WBTable, WBTableComp, WBTableProps} from '../WBTable/WBTable';
import {WBTableCellCreatedAt} from '../WBTable/WBTableCellCreatedAt';
import {WBTableCellDefault} from '../WBTable/WBTableCellDefault';
import {useDefaultKeys} from './hooks/useDefaultKeys';
import {useFilteredKeys} from './hooks/useFilteredKeys';
import {useRunSelectorRuns} from './hooks/useRunSelectorRuns';
import {useTopLevelQueryVariables} from './hooks/useTopLevelQueryVariables';
import {RunsAutoCols} from './RunsAutoCols';
import {RunsColumnEditor} from './RunsColumnEditor';
import {RunsDownloadMenu} from './RunsDownloadMenu';
import * as S from './RunSelectorInner.styles';
import {RunSelectorRowHeader} from './RunSelectorRowHeader';
import {RunsFreezeAndFilter} from './RunsFreeze';
import {RunsGroupTableAction} from './RunsGrouping';
import {RunsSortTableAction} from './RunsSort';
import {makeRunsSortIndicator} from './RunsSortIndicator';
import {RunsTableHeader} from './RunsTableHeader';

/**
 * RunSelectorInner is WBTable, wrapped with GQL queries for project info,
 * tags, and runs. For runs data, there's a top-level query to populate the
 * table and separate sub-queries for each expanded group (see WBTableRow)
 * and CSV download (see TableExport).
 */

const FIXED_COLUMNS = ['run:displayName'];
const CELL_FILTER_BLACKLIST: Run.Key[] = [
  {section: 'run', name: 'github'},
  {section: 'run', name: 'runInfo.gpu'},
  {section: 'run', name: 'runInfo.gpuCount'},
];

type RunSelectorFeature =
  | 'create-sweep'
  | 'push-to-run-queue'
  | 'toggle-visibility'
  | 'filter'
  | 'group'
  | 'sort'
  | 'tag'
  | 'move'
  | 'delete'
  | 'export'
  | 'auto-cols'
  | 'edit-cols';
export type DisabledFeaturesMap = {[F in RunSelectorFeature]?: boolean};

interface RunSelectorInnerProps {
  benchmarkMeta?: BenchmarkProject;
  className?: string;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  defaultKeys?: Run.Key[]; // fka baseKeys
  displayBenchmark?: boolean;
  enableFreezeRunset?: boolean;
  enableSetProject?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
  isInReport?: boolean;
  isSingleMode?: boolean;
  onSelectRunName?: (runId: string) => void;
  onSetExpanded?: (expanded: boolean) => void;
  pageEntityName: string;
  pageProjectName: string;
  pollInterval: number;
  recommendedGrouping?: Query.Grouping;
  runSetRef: RunSetTypes.Ref;
  selectedRunName?: string;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  tempSelectionsRef: TempSelectionsTypes.Ref;
  title?: string;

  // If true, users won't be able to edit filter, grouping, etc.
  workspaceReadOnly?: boolean;
  // If true, users won't be able to rename runs, etc.
  projectReadOnly?: boolean;

  disabledFeatures?: DisabledFeaturesMap;
  disableRunLinks?: boolean;
  onHeightChange?(): void;
}

type AllRunSelectorProps = RunSelectorInnerProps &
  ReturnType<typeof useRunSelectorProps>;

interface RunSelectorInnerState {
  tableMessage?: JSX.Element;
  creatingSweep: boolean;
  currentPage: number;
  sortPopupOpen: boolean;
}

class RunSelectorInnerComp extends React.Component<
  AllRunSelectorProps,
  RunSelectorInnerState
> {
  state: RunSelectorInnerState = {
    tableMessage: undefined,
    creatingSweep: false,
    currentPage: 1,
    sortPopupOpen: false,
  };

  didPropsOrStateChange = makeDidPropsOrStateChange({
    name: 'RunSelector',
    deep: [
      'runSelectorQuery',
      'sort',
      'runSetFull',
      'updateGroupRunTags',
      'runSet',
    ],
    debug: false,
    verbose: true,
  });

  tableRef = React.createRef<WBTableComp>();

  rows = memoize((rowData: Run.Run[], selectedRunName?: string) => {
    return rowData.map(r => {
      return {
        ...r,
        selectedRunName,
        runsetInfo: {
          id: this.props.runSetFull.id,
          name: this.props.runSetFull.name,
        },
        __address__: TableCols.makeRowAddressComponent(
          r.name,
          this.props.runSetFull.id
        ),
      };
    });
  });

  lastRowCount = 0;

  allColumns = memoize(
    (
      entityName: string,
      projectName: string,
      defaultKeys: Run.Key[],
      filteredKeys: Run.Key[],
      tableSettings: TableSettings,
      totalRowCount: number,
      firstActiveRunName: string | undefined,
      benchmarkMeta: BenchmarkProject | undefined,
      displayBenchmark: boolean | undefined
    ) => {
      const {setTableSettings, selectAll, selectNone, disableRunLinks} =
        this.props;
      const filteredKeyStrings = filteredKeys.map(k => Run.keyToString(k));
      const orderKeys = tableSettings.columnOrder
        .filter(k => !_.includes(filteredKeyStrings, k))
        .map(k => Run.keyFromString(k)!);

      return TableCols.unionKeys(defaultKeys, [
        ...filteredKeys,
        ...orderKeys,
      ]).map(key => {
        return TableCols.keyToColumn(key, {
          renderHeader: Run.keysEqual(key, {
            section: 'run',
            name: 'displayName',
          })
            ? (displayedRows: Run.Run[]) => {
                return (
                  <RunsTableHeader
                    isSingleMode={this.props.isSingleMode}
                    totalRowCount={totalRowCount}
                    displayedRows={displayedRows}
                    runSetRef={this.props.runSetRef}
                    tempSelectionsRef={this.props.tempSelectionsRef}
                    showTempSelection={
                      !this.props.isExpandable || !!this.props.isExpanded
                    }
                    onlyShowSelected={tableSettings.onlyShowSelected}
                    readOnly={this.props.workspaceReadOnly}
                    disabledFeatures={this.props.disabledFeatures}
                    isInReport={this.props.isInReport}
                    onAllItemsTempSelected={() => {
                      // Show the "select all runs in project?" prompt.
                      // TODO(views): This could maybe be factored out somehow.
                      if (totalRowCount > displayedRows.length) {
                        const isGrouped = !_.isEmpty(
                          this.props.groupSelections.grouping
                        );
                        this.setState({
                          tableMessage: (
                            <>
                              All <strong>{displayedRows.length}</strong>
                              {isGrouped ? ' groups ' : ' runs '} on this page
                              are selected.{' '}
                              <span
                                className="fake-link"
                                onClick={() => {
                                  this.props.tempSelectAll();
                                  this.clearTableMessage();
                                }}>
                                Select all
                                {isGrouped ? ' groups ' : ' runs '} in this
                                project
                              </span>
                            </>
                          ),
                        });
                      }
                    }}
                    onAllItemsTempUnselected={this.clearTableMessage}
                    // TODO: Cleanup step: move these callbacks into RunsTableHeader.
                    onVisibilitySelectNone={selectNone}
                    onVisibilitySelectAll={selectAll}
                    onSetOnlyShowSelected={newSetting => {
                      setTableSettings({
                        ...tableSettings,
                        onlyShowSelected: newSetting,
                      });
                      this.reload();
                    }}
                  />
                );
              }
            : undefined,
          renderCell: (
            column,
            row: RunHelpers2.WBTableRowWithRunSetInfo,
            {
              addFilter,
              toggleExpandedRow,
              recursionDepth,
              isGroup,
              isGroupExpanded,
              loadingChildren,
              isHoveringRow,
            }
          ) => {
            const isGroupRun = isGroup && row.group === row.name;
            const columnKey = column.key;
            const value = Run.getValue(row, columnKey);
            if (CELL_FILTER_BLACKLIST.some(f => Run.keysEqual(columnKey, f))) {
              return (
                <WBTableCellDefault columnKey={columnKey} cellValue={value} />
              );
            }

            if (columnKey.section === 'tags') {
              if (isGroup && !isGroupRun) {
                return '';
              }

              return (
                <Tailwind>
                  <div
                    data-test="icon-wrap"
                    className="flex items-center gap-4">
                    {row.tags.map((tag, index) => (
                      <WorkspaceTag
                        key={index}
                        tag={tag}
                        style={{
                          cursor: this.props.workspaceReadOnly
                            ? 'unset'
                            : 'pointer',
                        }}
                        onClick={() => {
                          if (!this.props.workspaceReadOnly && addFilter) {
                            addFilter(
                              {section: 'tags', name: tag.name},
                              '=',
                              true
                            );
                          }
                        }}
                        onRemoveClick={
                          this.props.workspaceReadOnly
                            ? undefined
                            : (e: React.SyntheticEvent) => {
                                e.stopPropagation();
                                isGroup
                                  ? this.deleteGroupTag(row, tag.name)
                                  : this.deleteTag(row, tag.name);
                              }
                        }
                      />
                    ))}
                    {!this.props.workspaceReadOnly && (
                      <WBPopupTrigger
                        popupContent={({close}) => (
                          <Tailwind>
                            <RunTagInfiniteDropdown
                              isMultiSelect
                              toggleSelection={async tag => {
                                for (const t of row.tags) {
                                  if (t.name === tag) {
                                    isGroup
                                      ? this.deleteGroupTag(row, tag)
                                      : this.deleteTag(row, tag);
                                    return;
                                  }
                                }

                                isGroup
                                  ? this.addGroupTag(row, tag)
                                  : this.addTag(row, tag);
                                close();
                              }}
                              selections={row.tags.map(t => t.name)}
                            />
                          </Tailwind>
                        )}>
                        {({anchorRef, setOpen, open}) => {
                          return (
                            <Button
                              className={classNames(
                                'tag-add-button-icon-only',
                                {hidden: !isHoveringRow && !open}
                              )}
                              data-test="tag-add-button"
                              onClick={() => setOpen(!open)}
                              ref={anchorRef}
                              variant="quiet"
                              size="small"
                              icon="add-new"
                            />
                          );
                        }}
                      </WBPopupTrigger>
                    )}
                  </div>
                </Tailwind>
              );
            }

            if (Run.keysEqual(columnKey, {section: 'run', name: 'state'})) {
              return (
                <WBTableCellDefault
                  columnKey={columnKey}
                  cellValue={row.state}
                  addFilter={addFilter}>
                  <RunState value={row.state} />
                </WBTableCellDefault>
              );
            }

            if (Run.keysEqual(columnKey, {section: 'run', name: 'createdAt'})) {
              return (
                <WBTableCellCreatedAt
                  createdAt={row.createdAt}
                  addFilter={addFilter}
                />
              );
            }

            if (
              Run.keysEqual(columnKey, {section: 'run', name: 'heartbeatAt'})
            ) {
              return (
                <WBTableCellDefault
                  columnKey={columnKey}
                  cellValue={formatTimestamp(row.heartbeatAt)}
                  addFilter={addFilter}
                />
              );
            }

            if (Run.keysEqual(columnKey, {section: 'run', name: 'updatedAt'})) {
              return (
                <WBTableCellDefault
                  columnKey={columnKey}
                  cellValue={formatTimestamp(row.updatedAt)}
                  addFilter={addFilter}
                />
              );
            }

            if (Run.keysEqual(columnKey, {section: 'run', name: 'duration'})) {
              return (
                <TimeDisplay
                  timestamp={new Date(row.createdAt)}
                  format="month_round"
                  now={new Date(row.heartbeatAt)}
                />
              );
            }

            if (Run.keysEqual(columnKey, {section: 'run', name: 'notes'})) {
              const readOnlyNotes =
                (isGroup && !isGroupRun) || this.props.workspaceReadOnly;
              return (
                <EditableLabel
                  data-test="add-notes-input"
                  placeholder={readOnlyNotes ? '-' : 'Add notes...'}
                  readOnly={readOnlyNotes}
                  onBlur={(newNotes: string) => {
                    isGroupRun
                      ? this.saveRunGroupNotes(row, newNotes)
                      : this.saveRunNotes(row, newNotes);
                  }}
                  serverText={row.notes}
                  showPopup
                />
              );
            }

            if (
              Run.keysEqual(columnKey, {
                section: 'run',
                name: 'sweep',
              })
            ) {
              return (
                <>
                  {row.sweep ? (
                    disableRunLinks ? (
                      <WBTableCellDefault
                        columnKey={columnKey}
                        cellValue={getSweepDisplayName(row.sweep)}
                        addFilter={addFilter}
                      />
                    ) : (
                      <Link
                        to={urls.sweep({
                          entityName,
                          projectName,
                          sweepName: row.sweep.name,
                        })}>
                        {getSweepDisplayName(row.sweep)}
                      </Link>
                    )
                  ) : (
                    '-'
                  )}
                </>
              );
            }

            if (
              Run.keysEqual(columnKey, {
                section: 'run',
                name: 'displayName',
              })
            ) {
              return (
                <RunSelectorRowHeader
                  benchmarkMeta={benchmarkMeta}
                  customRunColorsRef={this.props.customRunColorsRef}
                  disabledFeatures={this.props.disabledFeatures}
                  disableRunLink={disableRunLinks}
                  displayBenchmark={displayBenchmark}
                  displayTempCheckbox={!!isHoveringRow}
                  entityName={entityName}
                  groupSelectionsRef={this.props.runSet.groupSelectionsRef}
                  isFirstActiveRun={row.name === firstActiveRunName}
                  isGroup={isGroup}
                  isGroupExpanded={!!isGroupExpanded}
                  isInReport={this.props.isInReport}
                  isSingleMode={this.props.isSingleMode}
                  isTableCollapsed={this.props.isTableCollapsed}
                  loadingChildren={loadingChildren}
                  onSelectRunName={this.props.onSelectRunName}
                  projectName={projectName}
                  projectReadOnly={this.props.projectReadOnly}
                  recursionDepth={recursionDepth}
                  row={row}
                  selectedRunName={this.props.selectedRunName}
                  showArtifactCounts={this.props.showArtifactCounts}
                  showLogCounts={this.props.showLogCounts}
                  tempSelectionsRef={this.props.tempSelectionsRef}
                  workspaceReadOnly={this.props.workspaceReadOnly}
                  // Actual group selection logic is now pushed down to this component.
                  // We just want to know about changes so we can call hintClose.
                  // TODO(views): There is probably a better pattern for this?
                  onVisibilityCheckboxClick={() => {
                    if (this.tableRef && this.tableRef.current) {
                      this.tableRef.current.hintClose();
                    }
                  }}
                  onTempCheckboxClick={this.clearTableMessage}
                  onGroupButtonClick={() => {
                    if (isGroup) {
                      return (
                        toggleExpandedRow && toggleExpandedRow(row.__address__)
                      );
                    }
                  }}
                  saveRunName={this.saveRunName}
                  stopRun={id =>
                    this.props.stopRun({variables: {id}}).then(this.reload)
                  }
                  setLaunchDrawerRow={this.props.setLaunchDrawerRow}
                  isLaunchActive={this.props.isLaunchActive}
                />
              );
            }
            if (value != null) {
              return (
                <WBTableCellDefault
                  columnKey={columnKey}
                  cellValue={value}
                  addFilter={addFilter}
                />
              );
            }

            return '-';
          },
        });
      });
    },
    (newArgs, oldArgs) => {
      if (
        !Array.isArray(newArgs) ||
        !Array.isArray(oldArgs) ||
        newArgs.length !== oldArgs.length
      ) {
        return false;
      }
      const [
        entityName,
        projectName,
        defaultKeys,
        filteredKeys,
        tableSettings,
        totalRowCount,
        benchmarkMeta,
        displayBenchmark,
      ] = newArgs;
      const [
        oldEntityName,
        oldProjectName,
        oldDefaultKeys,
        oldFilteredKeys,
        oldTableSettings,
        oldTotalRowCount,
        oldBenchmarkMeta,
        oldDisplayBenchmark,
      ] = oldArgs;
      return (
        entityName === oldEntityName &&
        projectName === oldProjectName &&
        _.isEqual(defaultKeys, oldDefaultKeys) &&
        filteredKeys === oldFilteredKeys &&
        tableSettings === oldTableSettings &&
        totalRowCount === oldTotalRowCount &&
        _.isEqual(benchmarkMeta, oldBenchmarkMeta) &&
        displayBenchmark === oldDisplayBenchmark
      );
    }
  );

  getExtraTableActionsLeft = memoize(
    (
      entityName,
      hasEntityStorageEnabled,
      projectName,
      enableSetProject,
      enableFreezeRunset,
      groupSelections,
      username,
      tempSelectNone,
      tags,
      disabledFeatures,
      tableSettings: TableSettings
    ) => (
      <>
        {enableSetProject && (
          <ProjectPicker
            entityName={entityName}
            projectName={projectName}
            setProject={this.props.setProject}
          />
        )}
        <RunsSearchBar
          isGroupSearch={Query.isGroupedByGroup(groupSelections.grouping)}
          isInReport={this.props.isInReport}
          search={{
            query: this.props.runSet.search.query,
            isRegex: this.props.runSet.search.isRegex ?? false,
          }}
          setSearch={(query, isRegex) => {
            if (this.tableRef.current) {
              this.tableRef.current.resetPagination();
            }
            this.props.setSearch(query, isRegex);
          }}
        />
        {this.props.isLaunchActive && (
          <JobDrawerLocked
            open={this.props.launchDrawerJobRun != null}
            closeDrawer={this.props.closeLaunchConfigModal}
            entityName={entityName}
            projectName={projectName}
            jobId={this.props.launchDrawerJobRun?.jobId ?? ''}
            jobVersion={this.props.launchDrawerJobRun?.jobVersion ?? ''}
            defaultRunToClone={this.props.launchDrawerJobRun?.runName}
          />
        )}
        <S.TableActionButtonsWrapper>
          <RunsFreezeAndFilter
            enableFreezeRunset={enableFreezeRunset}
            filtersRef={this.props.runSet.filtersRef}
            entityName={entityName}
            isInReport={this.props.isInReport}
            projectName={projectName}
            username={username}
            compact={!!this.props.isExpandable && !this.props.isExpanded}
            onChange={() => {
              if (this.tableRef.current != null) {
                this.tableRef.current.resetPagination();
              }
            }}
            tableSettings={tableSettings}
          />
          <RunsGroupTableAction
            entityName={entityName}
            projectName={projectName}
            groupSelectionsRef={this.props.runSet.groupSelectionsRef}
            recommendedGrouping={this.props.recommendedGrouping}
            compact={!!this.props.isExpandable && !this.props.isExpanded}
            isInReport={this.props.isInReport}
            onGroupingChanged={() => {
              this.tableRef.current?.resetPagination();
              tempSelectNone();
            }}
            tableSettings={tableSettings}
          />
          <RunsSortTableAction
            entityName={entityName}
            projectName={projectName}
            sortRef={this.props.runSet.sortRef}
            compact={!!this.props.isExpandable && !this.props.isExpanded}
            isInReport={this.props.isInReport}
            open={this.state.sortPopupOpen}
            onOpen={() => this.setState({sortPopupOpen: true})}
            onClose={() => this.setState({sortPopupOpen: false})}
            onSortChanged={() =>
              this.tableRef.current && this.tableRef.current.resetPagination()
            }
            tableSettings={tableSettings}
          />
          {this.props.isExpandable && !this.props.isExpanded ? (
            <></>
          ) : (
            <>
              {/* BATCH TAGGING, MOVING, DELETING */}
              <BulkTagEditor
                // TODO(views): Here and below, this is a bad way to pass these counts through as it
                // won't always update.
                selectedCount={this.props.tempSelectedCount}
                isInReport={this.props.isInReport}
                tags={tags}
                addTag={(tag: string) => this.bulkUpdateTag('add', tag)}
                removeTag={(tag: string) => this.bulkUpdateTag('remove', tag)}
              />
              <RunMover
                entityName={entityName}
                // prevent users from trying to move runs to another entity if the entity is using external storage
                // because this operation does not move the run files
                requireSameEntity={hasEntityStorageEnabled}
                requireSameEntityReason={
                  "Can't move runs between entities when using external storage"
                }
                isInReport={this.props.isInReport}
                projectName={projectName}
                selectedCount={this.props.tempSelectedCount}
                refetch={() => this.reload()}
                onMove={(entity, proj) => this.moveRuns(entity, proj)}
              />
              <DeleteRunsButton
                selectedCount={this.props.tempSelectedCount}
                isInReport={this.props.isInReport}
                onDelete={(deleteArtifacts: boolean) =>
                  this.deleteRuns(deleteArtifacts)
                }
                outputArtifactsCount={this.props.tempOutputArtifactsCount}
                activeRunsCount={this.props.tempSelectedActiveCount}
              />
              {!disabledFeatures?.['create-sweep'] && (
                <CreateSweepButton onClick={this.createSweepWithPriorRuns} />
              )}
            </>
          )}
        </S.TableActionButtonsWrapper>
      </>
    )
  );

  getExtraTableActionsRight = memoize(
    (allQueryVariables, allColumns, tableSettings, currentPage) => {
      return (
        <>
          <RunsDownloadMenu
            entityName={this.props.pageEntityName}
            projectName={this.props.pageProjectName}
            pageQuery={allQueryVariables}
            tableSettings={tableSettings}
          />
          <RunsAutoCols
            query={allQueryVariables}
            tableSettings={tableSettings}
            isInReport={this.props.isInReport}
            setTableSettings={this.props.setTableSettings}
            currentPage={currentPage}
            columns={allColumns}
          />
        </>
      );
    }
  );

  saveRunNotes = _.debounce((run: Run.Run, newNotes: string) => {
    const currentNotes = run.notes;
    if (newNotes !== currentNotes) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: run.displayName,
        notes: newNotes,
      });
    }
  }, 500);

  saveRunGroupNotes = _.debounce((run: Run.Run, newNotes: string) => {
    const {group: name, tags} = run;
    const {entityName, projectName} = this.props;
    this.props.updateGroupRun({
      variables: {
        projectName,
        entityName,
        name,
        tags: tags.map(t => t.name),
        notes: newNotes,
      },
    });
  }, 500);

  updateSort = (updateFn: (sort: Query.Sort) => void) => {
    const {sort, setSort} = this.props;
    const newSort = produce(sort, updateFn);
    setSort(newSort);
  };

  clearTableMessage = (callback?: () => void) => {
    this.setState({tableMessage: undefined}, () => {
      if (callback) {
        callback();
      }
    });
  };

  shouldComponentUpdate(
    nextProps: RunSelectorInnerProps,
    nextState: RunSelectorInnerState
  ): boolean {
    return this.didPropsOrStateChange(
      [this.props, nextProps],
      [this.state, nextState]
    );
  }

  saveRunName = (run: Run.Run, newName: string) => {
    const currentName = run.displayName || run.name;
    if (newName !== currentName) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: newName,
        notes: run.notes,
      });
      window.analytics?.track('Run name edited');
    }
  };

  addTag = (run: Run.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!currentTagNames.includes(newTagName)) {
      this.props.updateRunTags(run, [...currentTagNames, newTagName]);
    }
  };

  addGroupTag = (run: Run.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (currentTagNames.includes(newTagName)) {
      return;
    }

    this.props.updateGroupRunTags(run, [...currentTagNames, newTagName]);
  };

  deleteTag = (run: Run.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (_.includes(currentTagNames, removeTagName)) {
      this.props.updateRunTags(
        run,
        currentTagNames.filter(t => t !== removeTagName)
      );
    }
  };

  deleteGroupTag = (run: Run.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!currentTagNames.includes(removeTagName)) {
      return;
    }
    this.props.updateGroupRunTags(
      run,
      currentTagNames.filter(t => t !== removeTagName)
    );
  };

  bulkUpdateTag(actionType: 'add' | 'remove', tagName: string): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();
    this.props
      .bulkUpdateRunTags({
        entityName,
        projectName,
        filters: JSON.stringify(queryFilters),
        [actionType === 'add' ? 'addTags' : 'removeTags']: [tagName],
      })
      .then(() => {
        // Refreshes the <BulkTagEditor> menu
        if (
          !this.props.bulkEditorProjectTagsQuery.loading &&
          this.props.bulkEditorProjectTagsQuery.refetch
        ) {
          this.props.bulkEditorProjectTagsQuery.refetch();
        }
      });
  }

  moveRuns(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string> {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    return this.props
      .moveRuns({
        variables: {
          sourceEntityName: entityName,
          sourceProjectName: projectName,
          destinationEntityName,
          destinationProjectName,
          filters: JSON.stringify(queryFilters),
        },
      })
      .then(response => {
        if (response.data == null || response.data.moveRuns == null) {
          throw new Error('Unexpected response');
        }
        return response.data.moveRuns.task.id;
      });
  }

  deleteRuns(deleteArtifacts: boolean): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    this.props
      .deleteRuns({
        variables: {
          entityName,
          projectName,
          filters: JSON.stringify(queryFilters),
          deleteArtifacts,
        },
      })
      .then(resp => {
        if (resp.data?.deleteRuns?.errors) {
          toast(resp.data.deleteRuns.errors, {type: 'error'});
        }
        this.reload();
      });
  }

  createSweepWithPriorRuns = () => this.setState({creatingSweep: true});

  combinedFilter = () => {
    const {filters, mergeFilters, runSet} = this.props;
    const groupTempSelection = this.props.groupTempSelection;

    return combinedFilter(
      filters,
      groupTempSelection,
      undefined,
      mergeFilters,
      runSet.search
    );
  };

  reload = () => {
    if (
      !this.props.runSelectorQuery.loading &&
      this.props.runSelectorQuery.refetch
    ) {
      this.props.runSelectorQuery.refetch();
    }
    // Refreshes the <BulkTagEditor> menu
    if (
      !this.props.bulkEditorProjectTagsQuery.loading &&
      this.props.bulkEditorProjectTagsQuery.refetch
    ) {
      this.props.bulkEditorProjectTagsQuery.refetch();
    }
  };

  columnEditor = (
    config: TableSettings,
    setTableSettings: (config: TableSettings) => void
  ) => {
    const {entityName, projectName} = this.props;
    return (
      <RunsColumnEditor
        entityName={entityName}
        projectName={projectName}
        config={config}
        fixedColumns={FIXED_COLUMNS}
        update={setTableSettings}
      />
    );
  };

  onSetCurrentPage = (currentPage: number) => {
    this.setState({currentPage});
  };

  openSortPopup = () => this.setState({sortPopupOpen: true});

  render() {
    const {
      benchmarkMeta,
      disabledFeatures,
      defaultKeys,
      dispatch,
      displayBenchmark,
      enableFreezeRunset,
      enableSetProject,
      entityName,
      filteredKeys,
      groupSelections,
      isLoadingRuns,
      isLoadingRunsPolled,
      loadMoreRuns,
      projectName,
      onHeightChange,
      runs,
      runSetFull,
      selectedRunName,
      setGrouping,
      tableSettings,
      tempSelections,
      tempSelectNone,
      topLevelQueryVariables,
      totalRuns,
    } = this.props;
    const {tableMessage, creatingSweep, currentPage} = this.state;

    if (creatingSweep) {
      dispatch(
        setCreateSweepArgs({
          runSet: runSetFull,
          tempSelections,
        })
      );
      window.analytics?.track('Create Sweep Clicked', {
        entityName,
        projectName,
        location: 'runs table',
      });
      return (
        <Redirect to={`/${entityName}/${projectName}/create-sweep`} push />
      );
    }

    const runSelectorQuery = this.props.runSelectorQuery;
    const username = this.props.viewer ? this.props.viewer.username : '';

    const project = runSelectorQuery.initialLoading
      ? undefined
      : runSelectorQuery.project;
    const hasEntityStorageEnabled =
      project?.entity.settings?.storageBucketInfo != null;

    let tags: TagState[] = [];
    const bulkTagsProject = this.props.bulkEditorProjectTagsQuery.initialLoading
      ? undefined
      : this.props.bulkEditorProjectTagsQuery.project;
    if (bulkTagsProject) {
      const counts: {[key: string]: number} = {};
      (bulkTagsProject.tagCounts || []).forEach(t => {
        counts[t.name] = t.count;
      });
      tags = (bulkTagsProject.tags || []).map(tag => ({
        name: tag.name,
        colorIndex: tag.colorIndex,
        count: counts[tag.name] || 0,
      }));
    }

    const rows = this.rows(runs, selectedRunName);
    const firstActiveRunName = runs.find(r => r.state === 'running')?.name;
    const totalRowCount = totalRuns;
    const allColumns = this.allColumns(
      entityName,
      projectName,
      defaultKeys,
      filteredKeys,
      tableSettings,
      totalRowCount,
      firstActiveRunName,
      benchmarkMeta,
      displayBenchmark
    );

    if (rows.length !== this.lastRowCount) {
      this.lastRowCount = rows.length;
      onHeightChange?.();
    }

    const tableProps: WBTableProps = {
      className:
        'run-selector' +
        (this.props.className ? ` ${this.props.className}` : '') +
        (this.props.isExpandable ? ` expandable` : '') +
        (this.props.workspaceReadOnly ? ` read-only` : ''),
      clearTableMessage: this.clearTableMessage,
      columns: allColumns,
      expandable: this.props.isExpandable,
      expanded: this.props.isExpanded,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
      filters: this.props.filters,
      fixedColumns: FIXED_COLUMNS,
      grouping: groupSelections.grouping,
      isInReport: this.props.isInReport,
      isSingleMode: this.props.isSingleMode,
      loading: isLoadingRuns && !isLoadingRunsPolled,
      onSetExpanded: this.props.onSetExpanded,
      readOnly: this.props.workspaceReadOnly,
      rows,
      setFilters: this.props.setFilters,
      setGrouping,
      setTableSettings: this.props.setTableSettings,
      showArtifactCounts: this.props.showArtifactCounts,
      showLogCounts: this.props.showLogCounts,
      SortIndicatorComponent: makeRunsSortIndicator(this.props.runSet.sortRef),
      tableLoadMore: loadMoreRuns,
      tableMessage,
      tableSettings,
      title: this.props.title,
      toggleExpandedRow: this.props.toggleExpandedRow,
      totalRowCount,
      updateSort: this.updateSort,
      extraTableActionsLeft: this.getExtraTableActionsLeft(
        entityName,
        hasEntityStorageEnabled,
        projectName,
        enableSetProject,
        enableFreezeRunset,
        groupSelections,
        username,
        tempSelectNone,
        tags,
        disabledFeatures,
        tableSettings
      ),
      extraTableActionsRight: this.getExtraTableActionsRight(
        topLevelQueryVariables,
        allColumns,
        tableSettings,
        currentPage
      ),
      columnEditor: this.columnEditor,
      onSetCurrentPage: this.onSetCurrentPage,
      openSortPopup: this.openSortPopup,
      topLevelQueryVariables,
    };

    return <WBTable tableRef={this.tableRef} {...tableProps} />;
  }
}

function useRunSelectorProps(props: RunSelectorInnerProps) {
  const viewer = ViewerHooks.useViewer();
  const runSet = ViewHooks.usePart(props.runSetRef);
  const runSetFull = ViewHooks.useWhole(props.runSetRef);

  const groupSelections = ViewHooks.useWhole(runSet.groupSelectionsRef);
  const tempSelections = ViewHooks.useWhole(props.tempSelectionsRef);
  const runSetFilters = ViewHooks.useWhole(runSet.filtersRef);
  const sort = runSetFull.sort;

  const toggleExpandedRow = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.toggleExpandedRowAddress
  );

  const tempSelectAll = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectAll,
    props.tempSelectionsRef,
    runSet.groupSelectionsRef
  );

  const tempSelectNone = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectNone,
    props.tempSelectionsRef,
    runSet.groupSelectionsRef
  );

  const entityName = runSet.project
    ? runSet.project.entityName
    : props.pageEntityName;

  const projectName = runSet.project
    ? runSet.project.name
    : props.pageProjectName;

  const groupTempSelection = useMemo(
    () => ({
      grouping: groupSelections.grouping,
      selections: tempSelections,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
    }),
    [
      groupSelections.grouping,
      groupSelections.expandedRowAddresses,
      tempSelections,
    ]
  );

  const {mergeFilters, sweep} = useRunsQueryContext();

  const runSelectorQuery = useRunSelectorQuery({
    entityName,
    projectName,
    filters: runSetFilters,
    mergeFilters,
    groupTempSelection,
    ...props,
  });
  const bulkEditorProjectTagsQuery = useBulkEditorProjectTagsQuery({
    entityName,
    projectName,
    filters: runSetFilters,
    mergeFilters,
    groupTempSelection,
    ...props,
  });

  const {tempSelectedCount, tempOutputArtifactsCount, tempSelectedActiveCount} =
    useSelectedCounts(props.runSetRef, props.tempSelectionsRef);

  const [stopRun] = Generated.useStopRunMutation();
  const updateRun = RunHooks.useUpdateRun();
  const updateRunTags = RunHooks.useUpdateRunTags();
  const bulkUpdateRunTags = RunHooks.useBulkUpdateRunTags();
  const [modifyRuns] = Generated.useModifyRunsMutation();
  const [moveRuns] = Generated.useMoveRunsMutation();
  const [deleteRuns] = Generated.useDeleteRunsMutation();
  const [updateGroupRun] = Generated.useUpsertRunGroupMutation();
  const updateGroupRunTags = RunHooks.useUpdateGroupRunTags(
    projectName,
    entityName
  );

  const runSetUpdate = ViewHooks.useViewAction(
    props.runSetRef,
    RunSetActions.update
  );

  const setGrouping = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.setGrouping
  );

  const setSort = ViewHooks.useViewAction(runSet.sortRef, SortActions.set);

  const selectAll = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.selectAll
  );

  const selectNone = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.selectNone
  );

  const setProject = useCallback(
    (newEntityName: string, newProjectName: string) =>
      runSetUpdate({
        project: {entityName: newEntityName, name: newProjectName},
      }),
    [runSetUpdate]
  );

  const setFilters = ViewHooks.useViewAction(
    runSet.filtersRef,
    FilterActions.set
  );

  const setSearch = useCallback(
    (query: string, isRegex: boolean) =>
      runSetUpdate({search: {query, isRegex}}),
    [runSetUpdate]
  );

  const setTableSettings = useCallback(
    (runFeed: TableSettings) => runSetUpdate({runFeed}),
    [runSetUpdate]
  );

  const defaultKeys = useDefaultKeys(props.defaultKeys, sweep);
  const filteredKeys = useFilteredKeys(runSelectorQuery);

  const tableSettings = useDeepMemo(
    useMemo(() => {
      return TableCols.cleanRunFeedConfig(
        runSet.runFeed || EMPTY_TABLE_SETTINGS,
        filteredKeys,
        defaultKeys,
        props.isInReport
      );
    }, [defaultKeys, filteredKeys, props.isInReport, runSet.runFeed])
  );

  const [launchDrawerJobRun, setLaunchDrawerJobRun] =
    React.useState<LaunchDrawerJobRun | null>(null);
  const setLaunchDrawerRow = useCallback((name: string, jobInfo?: JobInfo) => {
    if (jobInfo == null) {
      return;
    }
    setLaunchDrawerJobRun({
      runName: name,
      jobId: jobInfo.id,
      jobVersion: jobInfo.versionStr,
    });
  }, []);

  const closeLaunchConfigModal = useCallback(() => {
    setLaunchDrawerJobRun(null);
  }, []);

  const launchAgentQuery = Generated.useEntityAgentsQuery({
    variables: {entityName},
  });
  const isLaunchActive = useMemo(() => {
    return (
      launchAgentQuery.data?.entity?.launchProject?.launchAgents?.some(
        agent => {
          return ['POLLING', 'RUNNING'].includes(agent.agentStatus);
        }
      ) ?? false
    );
  }, [launchAgentQuery]);

  const dispatch = useDispatch();

  const isTableCollapsed = (props.isExpandable && !props.isExpanded) ?? false;

  const topLevelQueryVariables = useTopLevelQueryVariables({
    enableArtifactCounts: props.showArtifactCounts ?? false,
    entityName,
    groupSelections,
    isTableCollapsed,
    mergeFilters,
    projectName,
    runSet,
    runSetFilters,
    sort,
    tableSettings,
  });

  const {isLoadingRuns, isLoadingRunsPolled, loadMoreRuns, runs, totalRuns} =
    useRunSelectorRuns({
      disabled: runSelectorQuery.initialLoading,
      pageSize: tableSettings.pageSize || EMPTY_TABLE_SETTINGS.pageSize,
      topLevelQueryVariables,
      overrideOptions: {
        shouldOnlyShowSelected: Boolean(tableSettings.onlyShowSelected),
      },
    });

  return {
    bulkEditorProjectTagsQuery,
    bulkUpdateRunTags,
    closeLaunchConfigModal,
    defaultKeys,
    deleteRuns,
    dispatch,
    entityName,
    filteredKeys,
    filters: runSetFilters,
    groupSelections,
    groupTempSelection,
    launchDrawerJobRun,
    loadMoreRuns,
    isLaunchActive,
    isLoadingRuns,
    isLoadingRunsPolled,
    isTableCollapsed,
    mergeFilters,
    modifyRuns,
    moveRuns,
    projectName,
    runs,
    runSelectorQuery,
    runSet,
    runSetFull,
    selectAll,
    selectNone,
    setFilters,
    setGrouping,
    setLaunchDrawerRow,
    setProject,
    setSearch,
    setSort,
    setTableSettings,
    sort,
    stopRun,
    tableSettings,
    tempOutputArtifactsCount,
    tempSelectAll,
    tempSelectedActiveCount,
    tempSelectedCount,
    tempSelections,
    tempSelectNone,
    toggleExpandedRow,
    topLevelQueryVariables,
    totalRuns,
    updateGroupRun,
    updateGroupRunTags,
    updateRun,
    updateRunTags,
    viewer,
  };
}

export const RunSelectorInner = (props: RunSelectorInnerProps) => {
  const selectedProps = useRunSelectorProps(props);
  return <RunSelectorInnerComp {...props} {...selectedProps} />;
};
